import { Icon } from "@circle-react/components/shared/Icon";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { TypographyProps } from "@circleco/compass/components/Typography";
import { Typography as CompassTypography } from "@circleco/compass/components/Typography";

import { TippyV2 as Tippy } from "@/react/components/SettingsApp/CompassCompat/Tooltip";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { useTypographyFlag } from "@/react/components/SettingsApp/CompassCompat/Typography/shared";

const labelTypes = {
  xs: Typography.LabelXs,
  sm: Typography.LabelSm,
  md: Typography.LabelMd,
  default: Typography.LabelMd,
};

const getTypographyProps = (
  size: "xs" | "sm" | "md",
  weight?: "normal" | "medium" | "semibold",
): TypographyProps => {
  if (weight === "semibold") {
    if (size === "xs") {
      return {
        variant: "body-md",
        color: "secondary",
      };
    }

    return {
      variant: size === "sm" ? "label-sm" : "label-md",
      color: "primary",
    };
  }

  if (size === "xs") {
    return {
      variant: "label-xs",
      color: "secondary",
    };
  }

  return {
    variant: size === "sm" ? "body-sm" : "body-md",
    color: "secondary",
  };
};

export interface LabelProps {
  children: string | React.ReactNode;
  className?: string;
  size?: "xs" | "sm" | "md";
  tooltip?: string;
  weight?: "normal" | "medium" | "semibold";
}

export const Label = ({
  children,
  className = "",
  size = "sm",
  tooltip,
  weight = "normal",
}: LabelProps) => {
  const isTypographyFlagEnabled = useTypographyFlag();

  const tooltipNode = tooltip && (
    <Tippy
      dataTestId="checkout-form-preview-entry-tooltip"
      className="inline-flex cursor-help"
      content={tooltip}
    >
      <Icon type="16-question-mark" size={16} color="text-primary" />
    </Tippy>
  );

  if (isTypographyFlagEnabled) {
    return (
      <div className="flex flex-row gap-x-1 whitespace-normal">
        <CompassTypography {...getTypographyProps(size, weight)}>
          <span className="flex flex-row gap-x-2 whitespace-normal">
            {children}
          </span>
        </CompassTypography>
        {tooltipNode}
      </div>
    );
  }

  const LabelComponent = labelTypes[size] || labelTypes["default"];
  return (
    <div className="flex flex-row gap-x-2 whitespace-normal">
      <LabelComponent weight={weight}>
        <span className={classNames(className)}>{children}</span>
      </LabelComponent>
      {tooltipNode}
    </div>
  );
};
